import {
  SceneData,
  CrimeSceneData,
  CharacterTransform,
  SceneItem,
  FrameObject,
  Position,
  Quaternion,
} from "@/types/crime";
import {
  ALL_SCENES_DATA,
  AVATAR_DATA,
  CRIME_SCORE_RESULT,
} from "src/const/crimeSceneConst";

import * as THREE from "three";

export function getSceneDataBySceneIndex(index: number): SceneData {
  return (
    ALL_SCENES_DATA.find((data) => data.sceneIndex === index) ??
    ALL_SCENES_DATA[0]
  );
}

export function getDataByName(dataArray: any[], name: string) {
  return dataArray.find((data) => data.name === name) || null;
}

export function getTimeIndex(array: number[], time: number) {
  let targetIndex = -1;
  for (let index = 0; index < array.length; index++) {
    if (time <= array[index]) {
      targetIndex = index;
      break;
    }
  }
  if (targetIndex == -1) {
    targetIndex = array.length - 1;
  }
  return targetIndex;
}

export function getPercentageIndex(array: number[], time: number) {
  if (time == 0) {
    return { index: 0, percentage: 0 };
  }

  let targetIndex = -1;
  let previousDataTime = -1;
  let dataTime = -1;

  for (let index = 0; index < array.length; index++) {
    if (time <= array[index]) {
      targetIndex = index;

      break;
    }
  }
  if (targetIndex == -1) {
    targetIndex = array.length - 1;
  }
  dataTime = array[targetIndex];
  previousDataTime = array[targetIndex - 1];
  const percentage = calculatePercentage(time, previousDataTime, dataTime);
  return { index: targetIndex, percentage: percentage };
}

export function reduceDataByFraction(
  data: any[],
  retainFraction: number
): any[] {
  const items = data;
  const length = items.length;
  const result: any[] = [];

  if (length === 0) return data;
  if (length <= 2 || retainFraction >= 1) return data;

  // Calculate how many items to keep (at least first and last)
  const keepCount = Math.max(2, Math.round(length * retainFraction));

  // Always add the first
  result.push(items[0]);

  // Calculate step size for sampling between first and last
  const step = (length - 2) / (keepCount - 2);

  for (let i = 1; i < keepCount - 1; i++) {
    const index = Math.floor(i * step);
    result.push(items[index + 1]);
  }

  // Always add last
  result.push(items[length - 1]);

  return result;
}

export function getTitleIndex(array: number[], time: number) {
  let targetIndex = -1;
  if (time == 0) {
    return array[array.length - 1];
  }
  for (let index = 0; index < array.length; index++) {
    const checkpointTime = array[index];
    if (time >= array[index]) {
      if (Math.abs(time - checkpointTime) < 1) {
        targetIndex = index;
        return array.length - 1 - targetIndex;
      }
      break;
    }
  }
  return -1;
}

// type CharacterName = keyof typeof CHARACTER_COLOR;
// export function getColorByName(name: CharacterName): string {
//   return CHARACTER_COLOR[name as CharacterName] || "#FFFFFF";
// }

export function getAvatarColorByType(type: string) {
  const data = AVATAR_DATA.find((data) => data.type === type) || null;
  return data?.color ?? "#FFFFFF00";
}

export function getAvatarImageByType(type: string) {
  const data = AVATAR_DATA.find((data) => data.type === type) || null;
  return data?.image ?? "";
}

export function getAvatarDisplayNameByType(type: string) {
  const data = AVATAR_DATA.find((data) => data.type === type) || null;
  return data?.displayName ?? "";
}

// export function getAvatarStatusByName(name: string){
//   const data = AVATAR_DATA.find((data) => data.name === name) || null;
//   return data?.status ? `(${data?.status})` : "";
// }

export function calculatePercentage(value: number, min: number, max: number) {
  if (min === max) {
    return 0;
  }
  const clampedValue = Math.max(min, Math.min(value, max));
  return (clampedValue - min) / (max - min);
}

export function calculateValue(min: number, max: number, percentage: number) {
  if (min === max) {
    return min;
  }
  return min + percentage * (max - min);
}

export function formatSecondsToHHmm(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600) % 24;
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const pad = (num: number) => num.toString().padStart(2, "0");
  return pad(hours) + ":" + pad(minutes);
}

export function findObjectByName(objects: any[], targetName: string) {
  return objects.find((obj) => obj.name === targetName);
}

export function findObjectByStringArrayName(
  data: string[],
  targetName: string
) {
  return data.find((obj) => {
    const [name] = obj.split(",");
    return name === targetName;
  });
}

export function createCharacterFrameObject(dataString: string): FrameObject {
  const [name, ...values] = dataString.split(",");
  const numbers = values.map(Number);

  const position: Position = {
    x: numbers[0],
    y: numbers[1],
    z: numbers[2],
  };

  const rotation: Quaternion = {
    x: numbers[3],
    y: numbers[4],
    z: numbers[5],
    w: numbers[6],
  };

  return {
    name,
    position,
    rotation,
  };
}

export function createFrameObjectWithPercentage(
  dataString: string,
  previousDataString: string,
  percentage: number
): FrameObject {
  const [name, ...values] = dataString.split(",");
  const numbers = values.map(Number);

  if (previousDataString === "") {
    return {
      name,
      position: { x: numbers[0], y: numbers[1], z: numbers[2] },
      rotation: { x: numbers[3], y: numbers[4], z: numbers[5], w: numbers[6] },
    };
  } else {
    const previousNumbers = previousDataString.split(",").slice(1).map(Number);

    const interpolateValues = (startArr: number[], endArr: number[]) =>
      endArr.map((endVal, i) =>
        calculateValue(startArr[i], endVal, percentage)
      );

    const interpolated = interpolateValues(previousNumbers, numbers);

    return {
      name,
      position: { x: interpolated[0], y: interpolated[1], z: interpolated[2] },
      rotation: {
        x: interpolated[3],
        y: interpolated[4],
        z: interpolated[5],
        w: interpolated[6],
      },
    };
  }
}

export function getYAngleDegree(fromVec: THREE.Vector3, toVec: THREE.Vector3) {
  const direction = new THREE.Vector3().subVectors(toVec, fromVec);
  direction.y = 0;
  direction.normalize();
  const reference = new THREE.Vector3(0, 0, 1);
  const angleRad = Math.atan2(direction.x, direction.z);
  const angleDeg = THREE.MathUtils.radToDeg(angleRad);
  return angleDeg.toFixed(2);
}

export function findScoreResult(score: number) {
  const result = CRIME_SCORE_RESULT.find((s) => score >= s.score);
  return result;
}
